import { useState } from "react";
import { rankItems } from "@/mocks/gossip";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-primary-600",
  down: "ri-arrow-down-line text-accent-700",
  flat: "ri-arrow-right-line text-foreground-400",
};

const topColors = ["bg-primary-500 text-background-50", "bg-secondary-500 text-background-50", "bg-accent-600 text-background-50"];

export default function RankSection() {
  const [likedSet, setLikedSet] = useState<Set<number>>(new Set());

  const toggle = (i: number) => {
    setLikedSet((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  return (
    <section id="rank" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-16 md:px-6 md:py-24">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr] lg:gap-12">
        <div>
          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            <i className="ri-fire-fill"></i>
            实时热度
          </span>
          <h2 className="mt-3 font-display text-3xl font-black text-foreground-900 md:text-4xl">
            全网吃瓜热度榜
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-500 md:text-base">
            根据全网讨论声量与评论热度实时生成，前十个瓜，个个都是网友的快乐源泉。
          </p>

          <div className="mt-8 space-y-3">
            {rankItems.slice(0, 5).map((it, i) => (
              <div
                key={it.rank}
                className={`flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-200 hover:shadow-soft ${
                  i === 0 ? "border-primary-200" : ""
                }`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-display text-sm font-black ${
                    topColors[i] ?? "bg-background-100 text-foreground-600"
                  }`}
                >
                  {it.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-900">{it.text}</p>
                  <p className="mt-0.5 text-xs text-foreground-400">讨论热度 {it.heat.toLocaleString("zh-CN")}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full ${trendIcon[it.trend]}`}>
                    <i className={trendIcon[it.trend].split(" ")[0]}></i>
                  </span>
                  <button
                    onClick={() => toggle(i)}
                    aria-label="点赞"
                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs transition-colors ${
                      likedSet.has(i) ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                    }`}
                  >
                    <i className={likedSet.has(i) ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-8">
          <div className="relative overflow-hidden rounded-3xl bg-primary-500 p-6 md:p-8">
            <img
              src="https://readdy.ai/api/search-image?query=cute%20cartoon%20people%20sitting%20around%20watching%20big%20screen%20eating%20popcorn%20watermelon%20flat%20illustration%20warm%20colors%20fun%20community%20gathering&width=700&height=520&seq=rank-card&orientation=squarish"
              alt="吃瓜群众围坐围观热点"
              className="absolute inset-0 h-full w-full object-cover opacity-25"
            />
            <div className="relative">
              <p className="font-display text-lg font-bold text-background-50">吃瓜小贴士</p>
              <p className="mt-3 text-sm leading-7 text-background-50/95">
                吃瓜一时爽，一直吃瓜一直爽。但记得用时间管理大法给正经事留点位置——别问我是怎么知道的。
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["评论区见", "神回复", "每日三档"].map((t, idx) => (
                  <span
                    key={idx}
                    className="whitespace-nowrap rounded-full bg-background-50/25 px-3 py-1 text-xs font-medium text-background-50"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-3xl bg-background-100 p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-flashlight-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-bold text-foreground-900">看热闹开小灶</p>
                <p className="text-xs text-foreground-500">高能热评背后的冷知识</p>
              </div>
            </div>
            <p className="text-sm leading-7 text-foreground-700">
              你以为你只是在刷段子？其实你在掌握年轻人的语言密码。存几张图、记几句梗，就能和任何群聊无缝接轨。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}